// src/pages/RecordDetail.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api } from "../services/api";
import logo from "../assets/Logo_DMARCOne.png"; // Logo DMARCONE

// Utilidad para interpretar booleanos "raros"
function toBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return ["true", "t", "1", "yes", "y", "ok", "si", "sí", "pass"].includes(s);
}

// Pastilla visual simple
function BoolPill({ value }) {
  const on = toBool(value);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        on ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      {on ? "Sí" : "No"}
    </span>
  );
}

export default function RecordDetail() {
  const { recordid } = useParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;

    async function run() {
      try {
        setLoading(true);
        setErr("");

        const res = await api.get("/detalle_recordid_dmarc", {
          params: { recordid },
        });

        const json = res.data;
        if (!cancel) setData(Array.isArray(json) ? json : []);
      } catch (e) {
        if (cancel) return;

        const status = e.response?.status;
        if (status === 403) {
          setErr("No tienes permiso para ver el detalle (HTTP 403).");
        } else if (status === 401) {
          setErr("Sesión expirada o token inválido (HTTP 401).");
        } else {
          setErr(String(e.message || e));
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }

    run();
    return () => {
      cancel = true;
    };
  }, [recordid]);

  const header = useMemo(() => data[0] || {}, [data]);

  // 🔙 Botón Volver: cerrar pestaña (si el navegador lo permite)
  const handleBack = () => {
    window.close();
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#F5F5F5" }} // mismo fondo que el dashboard
    >
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {/* CABECERA SUPERIOR */}
        <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4">
            {/* Columna 1: título */}
            <div className="flex flex-col">
              <div className="text-xs text-gray-500">
                Detalle de autenticación
              </div>
              <h1
                className="text-lg md:text-xl font-semibold"
                style={{ color: "#414142" }}
              >
                RecordID #{recordid}
              </h1>
            </div>

            {/* Columna 2: logo centrado */}
            <div className="flex justify-center">
              <img
                src={logo}
                alt="DMARCONE"
                className="h-10 md:h-12 w-auto opacity-95"
              />
            </div>

            {/* Columna 3: botón volver */}
            <div className="flex justify-start md:justify-end">
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs md:text-sm font-medium shadow-sm border transition hover:bg-gray-100"
                style={{
                  borderColor: "#E5E7EB",
                  color: "#414142",
                }}
              >
                ← Volver al panel
              </button>
            </div>
          </div>
        </div>

        {/* ESTADOS */}
        {loading && (
          <div className="bg-white border border-gray-200 rounded-2xl p-4 text-sm text-gray-600 shadow-sm">
            Cargando detalle…
          </div>
        )}

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700 shadow-sm">
            Error: {err}
          </div>
        )}

        {/* CONTENIDO PRINCIPAL */}
        {!loading && !err && (
          <>
            {/* INFORMACIÓN GENERAL */}
            <section className="grid md:grid-cols-2 gap-4">
              {[
                ["IP Fuente", header.sourceip],
                ["Dominio (política)", header.domain || header.dominio],
                ["Origen (report org)", header.reportorgname],
                ["Total correos", header.count],
              ].map(([label, val], i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl shadow p-4 border border-gray-100"
                >
                  <div className="text-xs text-gray-500 mb-1">{label}</div>
                  <div className="text-sm text-gray-900">{val ?? "-"}</div>
                </div>
              ))}

              <div className="bg-white rounded-2xl shadow p-4 border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">DKIM Alineado</div>
                <BoolPill value={header.dkimaligned} />
              </div>

              <div className="bg-white rounded-2xl shadow p-4 border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">SPF Alineado</div>
                <BoolPill value={header.spfaligned} />
              </div>

              <div className="bg-white rounded-2xl shadow p-4 border border-gray-100 md:col-span-2">
                <div className="text-xs text-gray-500 mb-1">
                  Disposición DMARC
                </div>
                <div className="text-sm text-gray-900">
                  {header.disposition || "-"}
                </div>
              </div>
            </section>

            {/* TABLA DE AUTENTICACIONES */}
            <section
              className="rounded-2xl shadow p-5"
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
              }}
            >
              <h2
                className="text-lg font-medium mb-3"
                style={{ color: "#414142" }}
              >
                Autenticaciones
              </h2>

              <div className="overflow-auto rounded-xl border border-gray-100">
                <table className="min-w-full text-xs md:text-sm">
                  <thead>
                    <tr
                      className="text-left uppercase tracking-wide text-[10px] md:text-[11px]"
                      style={{ backgroundColor: "#00C4CC", color: "#FFFFFF" }}
                    >
                      <th className="px-3 py-2">AuthType</th>
                      <th className="px-3 py-2">Resultado</th>
                      <th className="px-3 py-2">ASN Org</th>
                      <th className="px-3 py-2">Cloud Guess</th>
                      <th className="px-3 py-2">Selector</th>
                      <th className="px-3 py-2">DKIM Domain</th>
                      <th className="px-3 py-2">SPF Domain</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.length ? (
                      data.map((row, i) => (
                        <tr
                          key={i}
                          className="border-b last:border-0 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-3 py-2 font-medium text-slate-800">
                            {row.authtype}
                          </td>
                          <td className="px-3 py-2">{row.result}</td>
                          <td className="px-3 py-2">{row.asn_org || "—"}</td>
                          <td className="px-3 py-2">
                            {row.cloud_guess || "—"}
                          </td>
                          <td className="px-3 py-2">{row.selector ?? "—"}</td>
                          <td className="px-3 py-2">{row.dkimdomain ?? "—"}</td>
                          <td className="px-3 py-2">{row.spfdomain ?? "—"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="px-3 py-3 text-center text-gray-500"
                          colSpan={7}
                        >
                          Sin autenticaciones
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
